import { Box, Typography } from "@mui/material"
import { darken, styled, useTheme } from "@mui/material/styles"
import { type ReactNode, isValidElement } from "react"

type VariantProps = keyof typeof paddingBlockMult

const paddingBlockMult = {
  small: 0.1,
  medium: 0.4,
  large: 0.6,
} as const

export interface DiscountChipProps {
  label: ReactNode
  backgroundColor?: string
  textColor?: string
  fontSize?: number
  variant?: VariantProps
}

export const DiscountChip = ({
  backgroundColor,
  textColor,
  label,
  fontSize = 16,
  variant = "medium",
}: DiscountChipProps) => {
  const {
    palette: { common },
  } = useTheme()
  const padding = fontSize / 16
  const cornerSize = padding * 0.6

  // Ensure colors are strings (Storybook color picker might send objects)
  const bgColor = String(backgroundColor ?? common.lightBlue)
  const txtColor = String(textColor ?? common.darkBlue)

  return (
    <Box display="inline-flex" position="relative">
      <LabelContainer
        backgroundColor={bgColor}
        fontSize={fontSize}
        cornerSize={cornerSize}
        padding={padding}
        paddingBlock={paddingBlockMult[variant]}
      >
        {isValidElement(label) ? (
          label
        ) : (
          <Typography
            component="span"
            /* use of display block because inline and block dont handle line-height the same way */
            display="block"
            fontWeight="600"
            fontSize={(theme) => theme.typography.pxToRem(fontSize)}
            color={txtColor}
          >
            {label}
          </Typography>
        )}
      </LabelContainer>
      <FoldContainer cornerSize={cornerSize}>
        <Fold backgroundColor={bgColor} />
      </FoldContainer>
    </Box>
  )
}

const LabelContainer = styled("div", {
  shouldForwardProp: (propName) =>
    ![
      "backgroundColor",
      "fontSize",
      "padding",
      "cornerSize",
      "paddingBlock",
    ].includes(String(propName)),
})<{
  backgroundColor: string
  fontSize: number
  padding: number
  cornerSize: number
  paddingBlock: number
}>(({ backgroundColor, theme, fontSize, padding, cornerSize, paddingBlock }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  paddingBlock: `${padding * paddingBlock}rem`,
  paddingInline: `${padding}rem ${padding * 0.5}rem`,
  minWidth: theme.typography.pxToRem(fontSize * 4),
  backgroundColor,
  clipPath: `polygon(${cornerSize}rem 0, 100% 0, 100% 100%, 0 100%, 0 ${cornerSize}rem)`,
  position: "relative",
}))

const FoldContainer = styled("div", {
  shouldForwardProp: (propName) => propName !== "cornerSize",
})<{
  cornerSize: number
}>(({ cornerSize }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  width: `${cornerSize}rem`,
  height: `${cornerSize}rem`,
  filter: "drop-shadow(1px 1px 1.5px rgba(0,0,0,0.4))",
}))
const Fold = styled("div", {
  shouldForwardProp: (propName) => propName !== "backgroundColor",
})<{
  backgroundColor: string
}>(({ backgroundColor }) => ({
  width: "100%",
  height: "100%",
  background: darken(backgroundColor, 0.1),
  clipPath: "polygon(100% 0, 0 100%, 100% 100%)",
  pointerEvents: "none",
}))
